import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { casesAPI, documentsAPI, adminAPI, casesExtAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, Eye, FileText, Upload, History } from 'lucide-react';
import Timeline from '@/components/Timeline';
import ProgressRing from '@/components/ProgressRing';
import NegativationBlock from '@/components/NegativationBlock';
import MilestoneHistory from '@/components/MilestoneHistory';

export default function AdminCaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [milestoneHistory, setMilestoneHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = async () => {
    try {
      const res = await casesAPI.get(caseId);
      setCaseData(res.data);
      // Fetch milestone history
      try {
        const histRes = await casesExtAPI.milestoneHistory(caseId);
        setMilestoneHistory(histRes.data);
      } catch {}
    } catch (err) {
      toast.error('Erro ao carregar processo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [caseId]);

  const handleApprove = async (docId) => {
    try {
      await documentsAPI.approve(docId);
      toast.success('Documento aprovado!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovar documento');
    }
  };

  const handleReject = async (docId) => {
    try {
      await documentsAPI.reject(docId, 'Documento rejeitado pelo administrador');
      toast.success('Documento rejeitado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rejeitar documento');
    }
  };

  const handleAddMilestone = async (type) => {
    try {
      await adminAPI.addMilestone(caseId, { type });
      toast.success('Milestone adicionado!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar milestone');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="rn-spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  if (!caseData) {
    return <div className="text-center py-20 text-white/40">Processo nao encontrado</div>;
  }

  const milestoneTypes = new Set(caseData.milestones?.map(m => m.type) || []);
  const negativationStatus = {
    'SPC': milestoneTypes.has('NEG_SPC_CLEARED') ? 'cleared' : 'pending',
    'BOA_VISTA': milestoneTypes.has('NEG_BOA_VISTA_CLEARED') ? 'cleared' : 'pending',
    'CENPROT': milestoneTypes.has('NEG_CENPROT_CLEARED') ? 'cleared' : 'pending',
    'SERASA': milestoneTypes.has('NEG_SERASA_CLEARED') ? 'cleared' : 'pending',
  };

  return (
    <div className="space-y-6" data-testid="admin-case-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/cases')} className="p-2 rounded-xl hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-semibold">{caseData.user?.name || 'Cliente'}</h1>
          <p className="text-sm text-white/50">{caseData.docType}: {caseData.docFormatted}</p>
        </div>
        <span className={`badge-${caseData.progress >= 100 ? 'success' : 'neutral'} text-sm`}>
          {caseData.progress}%
        </span>
      </div>

      {/* Progress + Timeline Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <ProgressRing progress={caseData.progress} />
        </div>
        <div className="lg:col-span-8">
          <div className="rn-card h-full">
            <h3 className="font-display text-lg font-semibold mb-4">Linha do Tempo</h3>
            <Timeline milestones={caseData.milestones || []} />
          </div>
        </div>
      </div>

      {/* Admin Actions */}
      <div className="rn-card">
        <h3 className="font-display text-lg font-semibold mb-4">Acoes Administrativas</h3>
        <div className="flex flex-wrap gap-2">
          {!milestoneTypes.has('ACTION_FILED') && milestoneTypes.has('PROTOCOL_SENT') && (
            <button onClick={() => handleAddMilestone('ACTION_FILED')} className="btn-secondary text-sm">Registrar Acao Ajuizada</button>
          )}
        </div>
      </div>

      {/* Documents */}
      <div className="rn-card">
        <h3 className="font-display text-lg font-semibold mb-4">Documentos</h3>
        {caseData.documents?.length === 0 ? (
          <div className="text-center py-6" data-testid="empty-state">
            <Upload size={32} className="mx-auto text-white/20 mb-2" />
            <p className="text-sm text-white/40">Nenhum documento enviado.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {caseData.documents?.map((doc) => (
              <div key={doc.id} className={`flex items-center gap-4 p-4 rounded-xl border ${
                doc.status === 'validado' ? 'bg-[rgba(0,255,163,0.03)] border-[rgba(0,255,163,0.15)]' :
                doc.status === 'divergente' ? 'bg-[rgba(239,68,68,0.03)] border-red-500/15' :
                doc.status === 'atencao' ? 'bg-[rgba(245,158,11,0.03)] border-[rgba(245,158,11,0.15)]' :
                'bg-white/[0.03] border-white/10'
              }`}>
                <FileText size={20} className="text-white/40" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.fileName}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={
                      doc.status === 'validado' ? 'badge-success' :
                      doc.status === 'divergente' ? 'badge-danger' :
                      doc.status === 'atencao' ? 'badge-warning' :
                      'badge-neutral'
                    }>
                      {doc.status === 'validado' ? 'Validado' :
                       doc.status === 'divergente' ? 'Divergente' :
                       doc.status === 'atencao' ? 'Recebido' :
                       doc.status || 'Processando'}
                    </span>
                    {doc.ocrValidation === 'match' && <span className="badge-success">OCR: OK</span>}
                    {doc.ocrValidation === 'mismatch' && <span className="badge-danger">OCR: Divergente</span>}
                    {doc.ocrValidation === 'inconclusive' && <span className="badge-warning">OCR: Inconclusivo</span>}
                  </div>
                  {doc.extractedDocDigits && (
                    <p className="text-xs text-white/40 mt-1">CPF detectado: {doc.extractedDocDigits}</p>
                  )}
                  {doc.validationReason && (
                    <p className="text-xs text-white/30 mt-0.5">{doc.validationReason}</p>
                  )}
                </div>
                <a href={documentsAPI.getFileUrl(doc.id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/5">
                  <Eye size={16} className="text-white/60" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Negativation Status */}
      <div className="rn-card">
        <h3 className="font-display text-lg font-semibold mb-4">Status das Negativacoes</h3>
        <NegativationBlock status={negativationStatus} />
      </div>

      {/* Milestone History (Audit) */}
      <div className="rn-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <History size={20} /> Historico de Marcos (Auditoria)
          </h3>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="btn-ghost text-xs"
          >
            {showHistory ? 'Ocultar' : 'Expandir'}
          </button>
        </div>
        {showHistory && milestoneHistory && (
          <MilestoneHistory milestones={milestoneHistory.milestones} />
        )}
        {!showHistory && (
          <p className="text-sm text-white/40">
            {milestoneHistory?.total || 0} marco(s) registrado(s). Clique em "Expandir" para ver o historico completo.
          </p>
        )}
      </div>
    </div>
  );
}
